/**
 * §7.4 — "A notification list with unread counts. Poll on an interval — no
 * WebSocket infrastructure for this."
 *
 * Six of the seven categories can fire as of 1b: `access_granted` from Phase 0,
 * `review_due` from 1a, and `approval_requested`, `approval_decided`, and
 * `acknowledgment_due` from the §7.6 and §7.7 workflows. `mention` still cannot
 * — §8.1's comments do not parse @-mentions yet — and `assignment` waits on
 * Phase 2's tasks, so an empty list is still not a bug.
 *
 * Every row is a sentence `summarize` in `notifications/describe.ts` produced.
 * Nothing here branches on a category or an event type, deliberately: the digest
 * email renders the same rows from the same function, and a second summariser
 * here would drift within a phase — with the first symptom being a digest that
 * says something different from the screen it links to.
 */

import { Link } from '@tanstack/react-router';
import { useMarkNotificationsRead, useNotifications } from '../api/queries.ts';
import { formatRelative } from '../components/format.ts';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { cn } from '../components/ui/cn.ts';
import { EmptyState, ErrorState, Loading } from '../components/ui/feedback.tsx';
import { Card, PageHeader } from '../components/ui/layout.tsx';

export function NotificationsPage() {
  const notifications = useNotifications();
  const markRead = useMarkNotificationsRead();

  if (notifications.isPending) return <Loading />;
  if (notifications.isError) return <ErrorState error={notifications.error} />;

  const { notifications: items, unreadCount } = notifications.data;

  return (
    <>
      <PageHeader
        title="Notifications"
        description="One inbox, whatever the source. The daily digest bundles the same rows into a single email per person per day rather than one email per category — and the weekly cadence rides Monday's digest instead of becoming a second send."
        actions={
          unreadCount > 0 ? (
            <Button
              variant="secondary"
              isPending={markRead.isPending}
              onPress={() => markRead.mutate(undefined)}
            >
              Mark all read
            </Button>
          ) : null
        }
      />

      <Card padded={false}>
        {items.length === 0 ? (
          <EmptyState
            title="Nothing here"
            description="This fills up when someone gives you access, asks you to review a document, decides on a review you asked for, a policy needs your acknowledgment, or one of your documents passes its review date."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-secondary">
            {items.map((item) => (
              <li
                key={item.id}
                className={cn(
                  'flex flex-col gap-1 px-5 py-4',
                  !item.readAt && 'bg-brand-primary/40',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {/* The dot is decoration; the word is what a screen reader
                      needs, so it is real text placed off-screen rather than an
                      aria-label on a span with no role to carry it. */}
                  {!item.readAt ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="size-2 shrink-0 rounded-full bg-brand-solid"
                      />
                      <span className="sr-only">Unread.</span>
                    </>
                  ) : null}
                  <span className="font-medium text-primary">{item.title}</span>
                  <Badge tone="outline" mono>
                    {item.category}
                  </Badge>
                  <span className="ml-auto text-xs text-tertiary">
                    {formatRelative(item.createdAt)}
                  </span>
                </div>
                {item.detail ? <p className="text-sm text-tertiary">{item.detail}</p> : null}
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="font-mono text-quaternary">{item.event.type}</span>
                  {item.path ? (
                    <Link to={item.path} className="link">
                      Open the subject
                    </Link>
                  ) : null}
                  {!item.readAt ? (
                    <Button size="sm" variant="tertiary" onPress={() => markRead.mutate([item.id])}>
                      Mark read
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
